import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Loader2, AlertCircle, CheckCircle2, FileArchive, Download } from 'lucide-react';

function LeitorLote({ mostrarAlerta }) {
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [fase, setFase] = useState(''); // 'upload' | 'leitura' | 'montando'
  const [uploadPct, setUploadPct] = useState(0);
  const [progresso, setProgresso] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setErro('Apenas arquivos ZIP são permitidos');
        return;
      }
      setArquivo(file);
      setErro(null);
      setResultado(null);
      setProgresso(null);
      setFase('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.toLowerCase().endsWith('.zip')) {
      setArquivo(file);
      setErro(null);
      setResultado(null);
      setProgresso(null);
      setFase('');
    } else {
      setErro('Apenas arquivos ZIP são permitidos');
    }
  };

  const processarZip = async () => {
    if (!arquivo) return;

    setProcessando(true);
    setErro(null);
    setResultado(null);
    setProgresso(null);
    setFase('upload');
    setUploadPct(0);
    setStatusMsg('Enviando ZIP para o servidor...');

    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      const token = localStorage.getItem('auth_token');

      // Fase 1: Upload com progresso via XMLHttpRequest
      const responseText = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadPct(pct);
            const mbEnviados = (e.loaded / 1024 / 1024).toFixed(0);
            const mbTotal = (e.total / 1024 / 1024).toFixed(0);
            setStatusMsg(`Enviando: ${mbEnviados}MB / ${mbTotal}MB`);
          }
        };

        xhr.onreadystatechange = () => {
          // Quando começa a receber resposta (upload concluído), mudar de fase
          if (xhr.readyState === 3 && fase !== 'leitura') {
            setFase('leitura');
            setStatusMsg('Upload concluído. Extraindo PDFs...');
          }
        };

        // Precisamos processar SSE manualmente
        let lastIndex = 0;
        xhr.onprogress = () => {
          const newText = xhr.responseText.substring(lastIndex);
          lastIndex = xhr.responseText.length;

          const lines = newText.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.substring(6));
                handleSSEEvent(event);
              } catch (e) { /* ignore parse errors */ }
            }
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Processar qualquer SSE restante
            const remaining = xhr.responseText.substring(lastIndex);
            const lines = remaining.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const event = JSON.parse(line.substring(6));
                  handleSSEEvent(event);
                } catch (e) { /* ignore */ }
              }
            }
            resolve(xhr.responseText);
          } else {
            try {
              const errData = JSON.parse(xhr.responseText);
              reject(new Error(errData.error || `Erro ${xhr.status}`));
            } catch {
              reject(new Error(`Erro ${xhr.status}`));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Erro de conexão'));
        xhr.ontimeout = () => reject(new Error('Timeout'));

        xhr.open('POST', '/api/faturas/leitura-lote');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 600000; // 10 min
        xhr.send(formData);
      });

    } catch (err) {
      setErro(err.message);
      setProcessando(false);
      setStatusMsg('');
      setFase('');
    }
  };

  const handleSSEEvent = (event) => {
    if (event.type === 'start') {
      setFase('leitura');
      setStatusMsg(`${event.total} PDFs encontrados. Lendo...`);
      setProgresso({ current: 0, total: event.total, processados: 0, erros: 0 });
    } else if (event.type === 'progress') {
      setProgresso({
        current: event.current,
        total: event.total,
        processados: event.processados,
        erros: event.erros,
        ultimoArquivo: event.arquivo,
        ultimaUc: event.uc
      });
      setStatusMsg(`Lendo ${event.current}/${event.total}: ${event.arquivo}`);
    } else if (event.type === 'status') {
      setFase('montando');
      setStatusMsg(event.message);
    } else if (event.type === 'done') {
      setResultado(event);
      setProcessando(false);
      setStatusMsg('');
      setFase('');
      mostrarAlerta && mostrarAlerta('Processamento concluído!', 'success');
    } else if (event.type === 'error') {
      setErro(event.message);
      setProcessando(false);
      setStatusMsg('');
      setFase('');
    }
  };

  const baixarResultado = () => {
    if (!resultado?.loteId) return;
    const token = localStorage.getItem('auth_token');
    // Download direto via link (não carrega na memória do browser)
    window.location.href = `/api/faturas/leitura-lote/download/${resultado.loteId}?token=${token}`;
  };

  // Calcular percentual geral
  let pctGeral = 0;
  if (fase === 'upload') {
    pctGeral = Math.round(uploadPct * 0.3); // upload = 0-30%
  } else if (fase === 'leitura' && progresso) {
    pctGeral = 30 + Math.round((progresso.current / progresso.total) * 60); // leitura = 30-90%
  } else if (fase === 'montando') {
    pctGeral = 95;
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
          <FileSpreadsheet size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Leitor de Faturas em Lote
        </h2>
        <p style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.9rem' }}>
          Envie um ZIP com faturas PDF. Elas serão lidas (não salvas) e um ZIP será gerado com Excel + PDFs renomeados por ordem de consumo.
        </p>
      </div>

      {/* Área de Upload */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !processando && inputRef.current?.click()}
        style={{
          border: '2px dashed #ccc', borderRadius: '12px', padding: '3rem 2rem', textAlign: 'center',
          cursor: processando ? 'not-allowed' : 'pointer',
          backgroundColor: arquivo ? '#f0fdf4' : '#fafafa',
          borderColor: arquivo ? '#86efac' : '#ccc', transition: 'all 0.2s'
        }}
      >
        <input ref={inputRef} type="file" accept=".zip" onChange={handleFileChange} style={{ display: 'none' }} disabled={processando} />
        {arquivo ? (
          <>
            <FileArchive size={48} color="#16a34a" />
            <p style={{ marginTop: '0.75rem', fontWeight: 600, color: '#16a34a' }}>{arquivo.name}</p>
            <p style={{ color: '#666', fontSize: '0.85rem' }}>{(arquivo.size / 1024 / 1024).toFixed(1)} MB</p>
          </>
        ) : (
          <>
            <Upload size={48} color="#999" />
            <p style={{ marginTop: '0.75rem', color: '#666' }}>Arraste um arquivo ZIP aqui ou clique para selecionar</p>
            <p style={{ color: '#999', fontSize: '0.8rem' }}>O ZIP deve conter faturas em PDF da Equatorial</p>
          </>
        )}
      </div>

      {/* Botão Processar */}
      {arquivo && !processando && !resultado && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button onClick={processarZip} style={{
            padding: '0.75rem 2rem', fontSize: '1rem', fontWeight: 600,
            backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '8px',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <FileSpreadsheet size={20} />
            Processar e Gerar Excel
          </button>
        </div>
      )}

      {/* Barra de Progresso */}
      {processando && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
            <span style={{ color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              {statusMsg}
            </span>
            <span style={{ fontWeight: 600 }}>{pctGeral}%</span>
          </div>

          {/* Barra visual */}
          <div style={{ width: '100%', height: '28px', backgroundColor: '#e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{
              width: `${pctGeral}%`, height: '100%',
              background: fase === 'upload'
                ? 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                : 'linear-gradient(90deg, #2e7d32, #4caf50)',
              borderRadius: '14px', transition: 'width 0.3s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: '0.75rem', fontWeight: 600
            }}>
              {fase === 'upload' && pctGeral > 8 ? `Upload ${uploadPct}%` : ''}
              {fase === 'leitura' && progresso && pctGeral > 8 ? `${progresso.current}/${progresso.total} PDFs` : ''}
              {fase === 'montando' ? 'Gerando ZIP...' : ''}
            </div>
          </div>

          {/* Fases */}
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.8rem' }}>
            <span style={{ color: fase === 'upload' ? '#3b82f6' : (fase !== 'upload' ? '#16a34a' : '#999'), fontWeight: fase === 'upload' ? 600 : 400 }}>
              {fase === 'upload' ? '● ' : (pctGeral > 30 ? '✓ ' : '○ ')}Upload
            </span>
            <span style={{ color: fase === 'leitura' ? '#3b82f6' : (pctGeral > 90 ? '#16a34a' : '#999'), fontWeight: fase === 'leitura' ? 600 : 400 }}>
              {fase === 'leitura' ? '● ' : (pctGeral > 90 ? '✓ ' : '○ ')}Leitura dos PDFs
            </span>
            <span style={{ color: fase === 'montando' ? '#3b82f6' : '#999', fontWeight: fase === 'montando' ? 600 : 400 }}>
              {pctGeral >= 100 ? '✓ ' : (fase === 'montando' ? '● ' : '○ ')}Montando ZIP
            </span>
          </div>

          {/* Contadores de leitura */}
          {progresso && (
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
              <span>Lidos: <strong style={{ color: '#16a34a' }}>{progresso.processados}</strong></span>
              {progresso.erros > 0 && <span>Erros: <strong style={{ color: '#dc2626' }}>{progresso.erros}</strong></span>}
              {progresso.ultimaUc && <span>Última UC: <strong>{progresso.ultimaUc}</strong></span>}
            </div>
          )}
        </div>
      )}

      {/* Erro */}
      {erro && (
        <div style={{
          marginTop: '1.5rem', padding: '1rem 1.5rem', backgroundColor: '#fef2f2',
          borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#dc2626'
        }}>
          <AlertCircle size={20} />
          <span>{erro}</span>
        </div>
      )}

      {/* Resultado + Download */}
      {resultado && (
        <div style={{
          marginTop: '1.5rem', padding: '1.25rem 1.5rem',
          backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <CheckCircle2 size={20} color="#16a34a" />
            <span style={{ fontWeight: 600, color: '#16a34a' }}>Processamento concluído!</span>
          </div>
          <div style={{ display: 'flex', gap: '2rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
            <div><span style={{ color: '#666' }}>PDFs no ZIP: </span><strong>{resultado.total}</strong></div>
            <div><span style={{ color: '#666' }}>Processados: </span><strong style={{ color: '#16a34a' }}>{resultado.processados}</strong></div>
            {resultado.erros > 0 && <div><span style={{ color: '#666' }}>Erros: </span><strong style={{ color: '#dc2626' }}>{resultado.erros}</strong></div>}
          </div>
          <button onClick={baixarResultado} style={{
            padding: '0.65rem 1.5rem', fontSize: '0.95rem', fontWeight: 600,
            backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: '8px',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <Download size={18} />
            Baixar ZIP (Excel + PDFs renomeados)
          </button>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default LeitorLote;
